import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import {
  MatLegacyDialog,
  MatLegacyDialogRef
} from '@angular/material/legacy-dialog';
import { ComponentRendering } from '@sitecore-jss/sitecore-jss-angular';
import { Subscription, take } from 'rxjs';
import { IAccount } from '../../../../transfers/services/internal-accounts.service';
import { ReferenceCodesService } from '../../../../transfers/services/reference-codes.service';
import {
  PaymentDetailsService,
  Transfer
} from '../../../services/payment-details.service';
import { AccountsService } from '../../../services/accounts.service';
import { CancelService } from '../../../services/cancel.service';
import { SentooService } from '../../../services/sentoo.service';
import { PaymentStatus } from '../../../services/status.service';
import {
  CreateSentooPaymentService,
  TransferModel
} from '../../../services/create.service';

@Component({
  selector: 'ab-payment-screen',
  templateUrl: './payment-screen.component.html',
  styleUrls: ['./payment-screen.component.scss']
})
export class PaymentScreenComponent implements OnInit, OnDestroy {
  @Input() rendering: ComponentRendering;
  @Output() setPaymentStatus: EventEmitter<PaymentStatus> = new EventEmitter();
  @Output() setMerchantInfo: EventEmitter<any> = new EventEmitter();
  @Output() setCancelReturnUrl: EventEmitter<string> = new EventEmitter();

  // Validate payment section
  @ViewChild('validatePayment') validatePayment: TemplateRef<any>;
  @ViewChild('cancelPayment') cancelPaymentPopup: TemplateRef<any>;

  public transfer: Transfer;
  public challengeCode = '';
  public challengeCodeError: boolean;
  public challengeCodeErrorMessage: string;

  public validatePaymentDialogRef: MatLegacyDialogRef<any>;
  public cancelPaymentDialogRef: MatLegacyDialogRef<any>;

  // Account list related
  public accounts = [];
  public showList = false;
  public selectedAccount: IAccount;

  // Category related
  public defaultValue = '';
  public categoryForm = new UntypedFormGroup({
    category: new UntypedFormControl(Validators.required),
    subCategory: new UntypedFormControl('', Validators.required)
  });
  public selectedAccountNumber = '';
  public selectedReferenceCode = '';
  public isSubmittingCancelPayment = false;

  public isLoading = false;
  public referenceCodes: any = [];
  public showCategory = true;
  private subscriptions: Subscription[] = [];

  constructor(
    private createSentooPaymentService: CreateSentooPaymentService,
    private dialog: MatLegacyDialog,
    private accountsService: AccountsService,
    private paymentDetailsService: PaymentDetailsService,
    private sentooService: SentooService,
    private referenceCodesService: ReferenceCodesService,
    private cancelService: CancelService
  ) {}

  get category() {
    return this.categoryForm.get('category');
  }

  get subCategory() {
    return this.categoryForm.get('subCategory');
  }

  @HostListener('click', ['$event.target'])
  onClick(target: HTMLElement): void {
    if (
      target.closest('.account-selector') ||
      target.closest('.account-info') ||
      target.closest('.account-selector')
    ) {
      this.showList != this.showList;
    } else {
      this.showList = false;
    }
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.subscriptions.push(
      this.sentooService
        .getEncryptedData()
        .pipe(take(1))
        .subscribe((encryptedData) => {
          this.subscriptions.push(this.paymentDetailsService.getPaymentDetails(encryptedData).subscribe(
            (transferResponse) => {
              this.isLoading = false;
              if (!transferResponse.transferId) {
                this.setPaymentStatus.emit('invalid');
              } else {
                this.transfer = transferResponse;
                this.accountsService.getAccounts().subscribe((accounts) => {
                  this.accounts = accounts;
                  this.selectedAccount = accounts[0];
                  this.referenceCodesService
                    .getAll(
                      this.transfer?.transactionType as any,
                      this.selectedAccount as any
                    )
                    .subscribe((referenceCodes) => {
                      this.referenceCodes =
                        this.referenceCodesService.format(referenceCodes);
                      this.category.setValue(this.referenceCodes[0]);
                      this.selectCategory();
                    });
                });
              }
            },
            (error) => {
              this.setPaymentStatus.emit('connection');
            }
          ));
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  onAccountChange(account: IAccount) {
    this.selectedAccount = account || {};
    this.toggleAccountList();
    setTimeout(() => (this.showList = false), 100);
  }

  toggleAccountList(): void {
    this.showList = !this.showList;
  }

  openCancelPayment() {
    this.cancelPaymentDialogRef = this.dialog.open(this.cancelPaymentPopup, {
      panelClass: 'popup-dialog-sentoo',
      disableClose: true
    });
  }

  closeCancelPaymentDialog() {
    this.cancelPaymentDialogRef.close();
  }

  cancelSentooPayment() {
    this.isSubmittingCancelPayment = true;
    this.subscriptions.push(
      this.cancelService
        .cancelPayment(this.transfer.transferId.toString())
        .subscribe((cancelResponse) => {
          // TODO transform to enum and not a string
          this.isSubmittingCancelPayment = false;
          this.cancelPaymentDialogRef?.close();
          this.validatePaymentDialogRef?.close();
          this.setPaymentStatus.emit('cancelled');
          this.setCancelReturnUrl.emit(cancelResponse?.url);
        })
    );
  }

  // Validate payment section
  confirmPayment() {
    if (this.challengeCodeError) {
      this.challengeCodeError = false;
    }
    const transferProperties = {
      amount: this.transfer.amount,
      beneficiaryAccount: this.transfer.beneficiaryAccount,
      beneficiaryCity: this.transfer.beneficiaryCity,
      beneficiaryHouseNumber: this.transfer.beneficiaryHouseNumber,
      beneficiaryInstitutionCode: this.transfer.beneficiaryInstitutionCode,
      beneficiaryInstitutionCodeValue:
        this.transfer.beneficiaryInstitutionCodeValue,
      beneficiaryInstitutionName: this.transfer.beneficiaryInstitutionName,
      beneficiaryName: this.transfer.beneficiaryName,
      beneficiaryPostalCode: this.transfer.beneficiaryPostalCode,
      beneficiaryStreet: this.transfer.beneficiaryStreet,
      currency: this.transfer.currency,
      description: this.transfer.description,
      destinationCountryIsoCode: null,
      sentooProcessorId: this.transfer.sentooProcessorId,
      sentooTransactionId: this.transfer.sentooTransactionId,
      transactionType: this.transfer.transactionType,
      transferId: this.transfer.transferId,
      valueDate: new Date().toISOString(),
      referenceCategoryNumber: this.selectedReferenceCode,
      sourceAccount: this.selectedAccount.accountNumber
    };
    this.isLoading = true;
    const transferObject: TransferModel = transferProperties;
    this.subscriptions.push(this.createSentooPaymentService
      .getChallengeNumber(transferObject)
      .subscribe(
        (challengeNumberResponse) => {
          if (challengeNumberResponse.challengeCode) {
            this.isLoading = false;
            this.challengeCode = challengeNumberResponse.challengeCode;
            this.openValidatePayment();
          } else {
            this.isLoading = false;
            this.challengeCodeError = true;
            this.challengeCodeErrorMessage =
              challengeNumberResponse.errorDescription;
          }
        },
        (error) => {
          this.isLoading = false;
        }
      ));
  }

  openValidatePayment() {
    this.validatePaymentDialogRef = this.dialog.open(this.validatePayment, {
      panelClass: 'popup-dialog-sentoo',
      disableClose: true
    });
  }

  // Category related
  selectCategory(): void {
    this.subCategory.setValue(
      this.category.value ? this.category.value.subcategory[0] : null
    );

    if (this.subCategory.value) {
      this.categoryForm.controls['subCategory'].enable();
    } else {
      this.categoryForm.controls['subCategory'].disable();
    }

    this.selectSubCategory();
  }

  selectSubCategory(event?): void {
    this.selectedReferenceCode =
      event?.value?.code || this.category?.value?.subcategory?.[0]?.code;
    this.subCategory.setValue(
      event ? event.value : this.category?.value?.subcategory?.[0]
    );
  }
}
